import React, { useCallback, useEffect, useRef } from 'react'
import { Navigate } from 'react-router'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_DANGER, TOAST_TYPE_DEFAULT, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import {
  type CodeVerificationFormValidationSchema,
  codeVerificationFormValidationSchema,
} from './codeVerificationFormValidationSchema'
import styles from './styles.module.scss'
type CodeVerificationForm = {
  code: string
}

const CodeVerification = () => {
  const [getClassName] = useNameFormatter('CodeVerification')
  const { track } = useWebTracking()

  const { email, verifyAuthCode, requestAuthCode } = useAuth()
  const showToast = useToast()
  const { handleSubmit, control, getValues } = useForm<CodeVerificationFormValidationSchema>({
    mode: 'all',
    resolver: yupResolver(codeVerificationFormValidationSchema),
  })

  const { isSubmitting, isValid } = useFormState({ control })
  const lastSubmittedCode = useRef<string | null>(null)

  const onSubmit = useCallback(
    async ({ code }: CodeVerificationForm) => {
      track('se_signup__verification_code__submitted', {
        button_id: 'auto_submit',
        button_identifier: 'empty',
        value_present: true,
      })

      if (code === lastSubmittedCode.current) {
        return
      }
      lastSubmittedCode.current = code
      if (!email) {
        return
      }

      try {
        await verifyAuthCode(code, email)
      } catch (error) {
        logError(error, {
          attributes: {
            action: 'verifyAuthCode',
            message: error ?? 'None',
            result: 'Error validating auth code',
          },
          eventName: 'verifyAuthCode',
        })

        track('se_signup__error_message__fired', {
          error_field: 'code',
          error_message_text: 'Error validating auth code',
        })

        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='Error validating auth code'
            id='sputnik.CodeVerification__LtxQsZ'
          />,
          {
            className: 'fs-unmask',
            autoHideDelay: 5000,
          },
        )
      }
    },
    [email, showToast, track, verifyAuthCode],
  )

  /* tracking start */

  useEffect(() => {
    track('se_signup__verification_code_screen__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  useEffect(() => {
    if (isValid && !isSubmitting && getValues('code') !== lastSubmittedCode.current) {
      handleSubmit(onSubmit)()
    }
  }, [getValues, handleSubmit, isSubmitting, isValid, onSubmit])

  const handleResendCode = async () => {
    logInfo('resendAuthCode', {
      attributes: {
        result: 'Resend auth code clicked',
        mfaMethod: 'email',
      },
      eventName: 'SignIn',
    })
    track('se_signup__verification_code_resend__submitted')
    try {
      await requestAuthCode(email ?? '')

      showToast(
        TOAST_TYPE_DEFAULT,
        <FormattedMessage
          defaultMessage='Code sent'
          id='sputnik.CodeVerification__HAsJVT'
        />,
      )
    } catch {
      track('se_signup__error_message__fired', {
        error_field: 'code',
        error_message_text: 'Failed to resend code.',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to resend code. Please try again.'
          id='sputnik.CodeVerification__j6yQ9W'
        />,
      )
    }
  }

  if (!email) {
    return <Navigate to={PATHNAME_CONSTS.AUTH_PATH} />
  }

  return (
    <div className={styles[getClassName('')]}>
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Check your email for a verification code'
          id='sputnik.CodeVerification__P2zdi6'
        />
      </div>
      <div className={styles[getClassName('code-prompt')]}>
        <FormattedMessage
          defaultMessage='A code was sent to {email}. It may take a few minutes to receive the code, which expires in 15 minutes.'
          id='sputnik.CodeVerification__U6/c+w'
          values={{
            email,
          }}
        />
      </div>
      <form
        className={styles[getClassName('form')]}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormTextInput
          className='fs-mask'
          control={control}
          disabled={isSubmitting}
          label={
            <FormattedMessage
              defaultMessage='Verification code'
              id='sputnik.CodeVerification__/6gLBc'
            />
          }
          name='code'
          placeholder='00000'
        />
      </form>
      <BasicButton
        className={styles[getClassName('resend-code')]}
        type={BASIC_BUTTON_TYPE_OUTLINED}
        onClick={handleResendCode}
      >
        <FormattedMessage
          defaultMessage='Resend code'
          id='sputnik.CodeVerification__ZD48TW'
        />
      </BasicButton>
    </div>
  )
}

export default CodeVerification
